'use client'

import { useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'

/**
 * Feature Flags Hook
 *
 * Centralised feature flag access. Mirrors v2 `composables/useFeatureFlags.ts`.
 *
 * Currently uses static defaults + mock permission shape until the real
 * `whoami` payload is wired into the auth store.
 *
 * Usage:
 *   const flags = useFeatureFlags()
 *   if (flags.enableDepartment) { ... }
 */

export interface FeatureFlags {
  enableShareSession: boolean
  enableRoom: boolean
  enableDepartment: boolean
  enableCsvMetadata: boolean
  enableHighlightBox: boolean
  enableExportChat: boolean
  enableModelSelection: boolean
  enableAgentSelection: boolean
  enableTag: boolean
  enableCrm: boolean
  enableBroadcast: boolean
  enableSentiment: boolean
  enableDashboardSessionCategory: boolean
  enableDashboardAiAnswerableRate: boolean
  enableAuditLog: boolean
  enableFortune: boolean
}

export type FeatureFlagGroup = 'phase_1_features' | 'phase_2_features' | 'phase_3_features'

const PHASE_1_FEATURES: FeatureFlags = {
  enableShareSession: true,
  enableRoom: false,
  enableDepartment: true,
  enableCsvMetadata: false,
  enableHighlightBox: false,
  enableExportChat: false,
  enableModelSelection: true,
  enableAgentSelection: false,
  enableTag: false,
  enableCrm: false,
  enableBroadcast: false,
  enableSentiment: false,
  enableDashboardSessionCategory: false,
  enableDashboardAiAnswerableRate: false,
  enableAuditLog: false,
  enableFortune: false,
}

const PHASE_2_FEATURES: FeatureFlags = {
  ...PHASE_1_FEATURES,
  enableTag: true,
  enableCrm: true,
  enableAuditLog: true,
  enableAgentSelection: true,
  enableFortune: true,
}

const FEATURE_FLAG_GROUPS: Record<FeatureFlagGroup, FeatureFlags> = {
  phase_1_features: PHASE_1_FEATURES,
  phase_2_features: PHASE_2_FEATURES,
  phase_3_features: {
    ...PHASE_2_FEATURES,
    enableBroadcast: true,
    enableSentiment: true,
    enableDashboardSessionCategory: true,
    enableDashboardAiAnswerableRate: true,
  },
}

export function useFeatureFlags(): FeatureFlags {
  // TODO: derive from auth store / chat-feature status when API is wired.
  const featureFlagGroup = process.env.NEXT_PUBLIC_FEATURE_FLAG_GROUP as FeatureFlagGroup
  return FEATURE_FLAG_GROUPS[featureFlagGroup] ?? FEATURE_FLAG_GROUPS.phase_1_features
}

export function useFeatureFlagRedirect(isEnabled: boolean, redirectTo?: string): boolean {
  const router = useRouter()
  const pathname = usePathname() ?? '/'
  const fallbackPath = redirectTo ?? (pathname.startsWith('/admin') ? '/admin' : '/')

  useEffect(() => {
    if (!isEnabled) {
      router.replace(fallbackPath)
    }
  }, [fallbackPath, isEnabled, router])

  return isEnabled
}

// ---------- Permissions ----------

export const ADMIN_PERMISSIONS = {
  ACCESS_ADMIN_PAGE: 'permission_access_admin_page',
  READ_USER_CONVERSATION: 'permission_read_user_conversation',
  PAYMENT: 'permission_payment',
} as const

export type AdminPermission = (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS]

/**
 * Mock permission resolver. Returns a Set the layout can check against.
 * Replace with real `whoami.role.role_permissions` when auth is live.
 */
export function useAdminPermissions(): Set<string> {
  return new Set<string>([
    ADMIN_PERMISSIONS.ACCESS_ADMIN_PAGE,
    ADMIN_PERMISSIONS.READ_USER_CONVERSATION,
    ADMIN_PERMISSIONS.PAYMENT,
  ])
}
